import { Component, OnInit, AfterViewInit, ViewChild, HostListener, Input, signal, ChangeDetectionStrategy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { BizDataGridDirective } from '../shared/grid/biz-grid.directive';

import { PartnerSearch } from './models/partner-search';
import { PartnerSearchSettings } from './models/partner-search-settings';
import { PartnerService } from './services/partner.service';
import { PartnerExportUiService } from './services/partner-export-ui.service';
import { StateService } from '../shared/state-management/state.service';

import { DateTimeService } from '../shared/datetime/datetime.service';
import { CurrentUserService } from '../user/services/current-user.service';
import { ConfigurationService } from '../configuration/services/configuration.service';
import { TagSelectorComponent } from '../tags';

import { DiceComponent } from '../shared/easter/egg/nog/dice/dice.component';
import { SnakeComponent } from '../shared/easter/egg/nog/snake/snake.component';
import { StarwarsComponent } from '../shared/easter/egg/nog/starwars/starwars.component';
import { PacmanComponent } from '../shared/easter/egg/nog/pacman/pacman.component';
import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';
import { SvgIconComponent } from '../shared/icons/svg-icon.component';
import { RestrictedSearchCalloutComponent } from '../shared/restricted-search/restricted-search.component';
import { FormsModule } from '@angular/forms';
import { TagSelectorComponent as TagSelectorComponent_1 } from '../tags/tag-selector.component';
import { DxDataGridModule } from 'devextreme-angular';
import { DxoStateStoringModule, DxiColumnModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../shared/grid/biz-grid-date.directive';
import { BizGridFiltervalues } from '../shared/grid/biz-grid-filtervalues';

export class PartnerSearchStateObject {
    constructor(
        public foundPartners: PartnerSearch[],
        public isSearchResultRestricted: boolean,
        public searchSettings: PartnerSearchSettings,
        public refreshData: boolean = false,
        public isSearchMaxResultReached: boolean = false) {
    }
}

@Component({
    standalone: true,
    selector: 'partner-search',
    templateUrl: './partner-search.component.html',
    styleUrl: './partner-search.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DiceComponent, SnakeComponent, StarwarsComponent, PacmanComponent, PermissionRequiredDirective, SvgIconComponent, RestrictedSearchCalloutComponent, FormsModule, TagSelectorComponent_1, DxDataGridModule, BizDataGridDirective, DxoStateStoringModule, DxiColumnModule, BizUserColumnDirective, BizDateColumnDirective]
})
export class PartnerSearchComponent implements AfterViewInit, OnInit {
    @ViewChild(BizDataGridDirective) dataGrid: BizDataGridDirective;
    @ViewChild('partnerTagSelector') partnerTagSelector: TagSelectorComponent;

    public searchSettings: PartnerSearchSettings = new PartnerSearchSettings();
    public foundPartners: PartnerSearch[] = [];
    public isSearchResultRestricted = false;
    public selectedRows: PartnerSearch[] = [];
    public isSearching = false;
    public dateFormat: any;
    public showProxyPartnerColumn = signal(false);
    public showPartnerOnboardingColumn = signal(false);
    public rollDice = false;
    public showProductTeam = false;
    public showPacman = false;
    public showSnake = false;
    public selectedTags: any[] = [];
    public isSearchMaxResultReached = false;
    public stateStorageKey = 'partner-search-component-result-state';

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private partnerService: PartnerService,
        private stateService: StateService,
        private partnerExportService: PartnerExportUiService,
        private currentUserService: CurrentUserService,
        private dateTimeService: DateTimeService,
        private configurationService: ConfigurationService,
        public bizGridFiltervalues: BizGridFiltervalues) {
        this.dateFormat = this.dateTimeService.getDevExpressDateTimeFormat();
    }

    ngOnInit(): void {
        this.activatedRoute.params.subscribe(async (parms: any) => {
            const stateObject = this.stateService.getState(this.stateStorageKey) as PartnerSearchStateObject;
            if (stateObject) {
                this.foundPartners = stateObject.foundPartners;
                this.isSearchResultRestricted = stateObject.isSearchResultRestricted;
                this.isSearchMaxResultReached = stateObject.isSearchMaxResultReached;
                this.searchSettings = stateObject.searchSettings;
                if (stateObject.refreshData) {
                    this.search();
                }

            }
            else {
                try {
                    const disabledPreLoad = await this.configurationService.getEffectiveSettingValue('DisablePartnerPreLoad');

                    if (!disabledPreLoad || disabledPreLoad.toLowerCase() !== 'true') {
                        this.search();
                    }
                } catch (e) {
                    console.error(e);
                }
            }
        });
    }

    @HostListener('document:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            this.showPacman = this.showProductTeam = this.rollDice = this.showSnake = false;
        }
    }

    ngAfterViewInit(): void {
        this.dataGrid.rowDoubleClick.subscribe((partner: PartnerSearch) => {
            if (this.currentUserService.hasPermission('Partner_View_PartnerDetails')) {
                this.selectedRows = [partner];
                this.showPartners();
            }
        });

        this.initializeContextMenu();

        // For some reason needed. Otherwise the columns will disappear when the component is invoked again.
        // This might be because some logic are moved from this ngAfterViewInit to ngOnInit
        this.dataGrid.instance.refresh();
    }

    initializeContextMenu(): void {
        this.dataGrid.instance.option('onContextMenuPreparing', (e) => {
            if (e.row && e.row.rowType === 'data') {
                e.items = [];

                if (this.currentUserService.hasPermission('Partner_Action_PartnerExport')) {
                    e.items.push({
                        text: 'Export selected partners',
                        onItemClick: () => this.exportPartners()
                    });
                }

                if (this.selectedRows.length === 1) {
                    e.items.push({
                        text: 'Show distributions for partner',
                        onItemClick: () => {
                            const tempSearchObject = {
                                splitFromAndTo: false,
                                partnerFromId: e.row.key.PartnerId
                            };
                            this.router.navigate(['distribution/result', tempSearchObject]);
                        }
                    });
                }
            }
        });
    }

    search(): void {
        if (this.searchSettings.searchString === 'roll:dice') {
            this.rollDice = true;
        }
        else if (this.searchSettings.searchString === 'show:team') {
            this.showProductTeam = true;
        }
        else if (this.searchSettings.searchString === 'entertain:me') {
            this.showSnake = true;
        }
        else if (this.searchSettings.searchString === 'play:pacman') {
            this.showPacman = true;
        }
        else {
            this.showLoadingPanel();
            this.showPartnerOnboardingColumn.set(false);
            this.searchSettings.includePartnerOnboarding = true;
            this.searchSettings.searchTags = []; // Reset tags before adding new ones, if any are selected.
            this.selectedTags.forEach(s => {
                this.searchSettings.searchTags.push(s.value);
            });

            this.partnerService.searchPartners(this.searchSettings).then(partnerResult => {
                this.isSearchResultRestricted = partnerResult.IsPartnerSearchResultRestricted;
                this.isSearchMaxResultReached = partnerResult.MaxSearchResultReached;
                this.foundPartners = partnerResult.Partners;

                this.foundPartners.forEach(x => {
                    x.PartnerTags = x.TagsNames.join(', ');
                });

                const stateObject = new PartnerSearchStateObject(this.foundPartners, this.isSearchResultRestricted, this.searchSettings, this.isSearchMaxResultReached);
                this.stateService.setState(this.stateStorageKey, stateObject);

                this.showProxyPartnerColumn.set(this.foundPartners.some(p => p.IsProxyPartner));
                this.showPartnerOnboardingColumn.set(this.foundPartners.some(p => p.PartnerOnboardingStatus != null && p.PartnerOnboardingStatus.length > 0));

                this.hideLoadingPanel();
            }).catch(() => this.hideLoadingPanel());
        }
    }


    showLoadingPanel(): void {
        this.isSearching = true;
    }

    hideLoadingPanel(): void {
        this.isSearching = false;
    }

    clear(): void {
        this.searchSettings = new PartnerSearchSettings();
        this.foundPartners = [];
        this.selectedTags = [];
        this.partnerTagSelector.selectedTags = [];
        this.isSearchResultRestricted = false;
        this.includeLastActivityChange();
        this.dataGrid.reset();
    }

    includeLastActivityChange(): void {
        this.dataGrid.instance.columnOption('LatestDocumentDate', 'visible', this.searchSettings.includeLatesDocumentDate);
    }

    showPartners(): void {
        const partnerIds = this.selectedRows.map((p) => p.PartnerId);
        this.router.navigate(['partner/details', { partnerIds: partnerIds.join(',') }]);
    }

    createNew(): void {
        this.router.navigate(['partner/details', { createNew: true, force: new Date().getTime() }]);
    }

    selectionChanged(e: any): void {
        this.selectedRows = e.selectedRowsData;
    }

    selectedTagsChange(e: any): void {
        this.selectedTags = e;
    }

    get canExportPartners(): boolean {
        return (this.selectedRows.length >= 1);
    }

    exportPartners(): void {
        if (this.canExportPartners) {
            const partnerIds = this.selectedRows.map(e => e.PartnerId);
            this.partnerExportService.exportPartners(partnerIds);
        }
    }
}
